import React from 'react';
import { X } from 'lucide-react';

const ActivityPanel = ({ selectedRowId, rows, onClose }) => {
  if (!selectedRowId) return null;

  const row = rows.find(r => r.id === selectedRowId);
  
  if (!row) {
    return (
      <aside
        className="fixed top-0 right-0 h-full w-96 bg-white/95 backdrop-blur-sm shadow-2xl border-l border-gray-200/60 flex flex-col z-50"
        aria-label="Activity log panel"
      >
        <header className="px-6 py-4 border-b border-gray-200/60 bg-gradient-to-r from-purple-50 to-blue-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-bold text-gray-800">Activity Log</h2>
              <p className="text-xs text-gray-600">Row ID: {selectedRowId}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-600 hover:text-red-700 hover:bg-red-100 focus:text-red-700 focus:bg-red-100 focus:outline-none focus:ring-4 focus:ring-red-200 rounded-lg transition-all duration-200"
              aria-label="Close activity log panel"
            >
              <X className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="text-gray-500 text-center">Row not found.</div>
        </div>
      </aside>
    );
  }

  return (
    <aside
      className="fixed top-0 right-0 h-full w-96 bg-white/95 backdrop-blur-sm shadow-2xl border-l border-gray-200/60 flex flex-col z-50"
      aria-label="Activity log panel"
    >
      <header className="px-6 py-4 border-b border-gray-200/60 bg-gradient-to-r from-purple-50 to-blue-50">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-gray-800">Activity Log</h2>
            <p className="text-xs text-gray-600">Row ID: {selectedRowId}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-600 hover:text-red-700 hover:bg-red-100 focus:text-red-700 focus:bg-red-100 focus:outline-none focus:ring-4 focus:ring-red-200 rounded-lg transition-all duration-200"
            aria-label="Close activity log panel"
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>
      </header>
      
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {/* Summary Section */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-4">
          <div className="text-sm font-bold text-gray-800 mb-3">Summary</div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
            <div className="text-gray-600 font-medium">Code:</div>
            <div className="font-mono font-bold">{row.code}</div>
            <div className="text-gray-600 font-medium">Client:</div>
            <div>{row.client || '—'}</div>
            <div className="text-gray-600 font-medium">Page:</div>
            <div>{row.page || '—'}</div>
            <div className="text-gray-600 font-medium">Section:</div>
            <div>{row.section || '—'}</div>
            <div className="text-gray-600 font-medium">Employee:</div>
            <div>{row.employee || '—'}</div>
            <div className="text-gray-600 font-medium col-span-2">File Name:</div>
            <div className="col-span-2 break-all font-mono text-[10px] bg-white p-2 rounded-lg border">
              {row.fileName || 'Auto-generated'}
            </div>
            <div className="text-gray-600 font-medium">Created:</div>
            <div className="text-[10px]">{new Date(row.created).toLocaleString()}</div>
            <div className="text-gray-600 font-medium">Updated:</div>
            <div className="text-[10px]">{new Date(row.updated).toLocaleString()}</div>
          </div>
        </div>

        {/* Changes Section */}
        <div>
          <div className="text-sm font-bold text-gray-800 mb-3">
            Changes ({(row.history || []).length})
          </div>
          <div className="space-y-3">
            {(row.history || []).length === 0 && (
              <div className="text-xs text-gray-500 text-center italic">No activity yet.</div>
            )}
            {(row.history || []).slice().reverse().map((h, i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-3 bg-white shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-[10px] text-gray-500" title={h.ts}>
                    {new Date(h.ts).toLocaleString()}
                  </span>
                  <span className="text-[10px] px-2 py-1 rounded-full bg-purple-100 text-purple-700 border border-purple-200 font-medium">
                    {h.editor}
                  </span>
                </div>
                {h.field === '_init' ? (
                  <div className="text-xs text-gray-700 font-medium">{h.newValue}</div>
                ) : (
                  <div className="text-xs text-gray-800">
                    <span className="font-bold text-purple-600">{h.field}</span>: 
                    <span className="text-gray-500 mx-1">{h.oldValue || '∅'}</span> 
                    → 
                    <span className="text-gray-800 font-medium mx-1">{h.newValue || '∅'}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </aside>
  );
};

export default ActivityPanel;